import { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Edit, Trash2, Users, Check } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatNumber } from '@/common/lib/utils'
import { PlayerTagResponse } from '../../types'
import { GetPresetIdResponse } from '@/modules/promotion/type'

export const columns = (
	hasWritePermission: boolean,
	onEdit: (tag: PlayerTagResponse) => void,
	onDelete: (tag: PlayerTagResponse) => void,
	presetsData?: GetPresetIdResponse,
): ColumnDef<PlayerTagResponse>[] => [
	{
		accessorKey: 'name',
		header: 'ชื่อแท็ก',
		cell: ({ row }) => (
			<div className="flex items-center gap-2">
				<Badge
					variant="outline"
					style={{ backgroundColor: row.original.color || '#e5e7eb' }}
					className="text-white"
				>
					{row.original.name}
				</Badge>
			</div>
		),
	},
	{
		accessorKey: 'minDepositAmount',
		header: 'เงื่อนไขอัตโนมัติ',
		cell: ({ row }) => {
			if (row.original.depositGtWithdraw) {
				return (
					<div className="flex items-center gap-2">
						<Check className="h-4 w-4 text-green-600" />
						<span className="text-sm">ฝาก {'>'} ถอน</span>
					</div>
				)
			}
			if (row.original.minDepositAmount) {
				return (
					<div className="text-sm">
						ยอดฝาก ≥ ฿{formatNumber(row.original.minDepositAmount)}
					</div>
				)
			}
			return <span className="text-sm text-muted-foreground">-</span>
		},
	},
	{
		accessorKey: 'presetId',
		header: 'RTP Preset',
		cell: ({ row }) => {
			const hasPreset = row.original.presetId || row.original.presetNumber
			if (!hasPreset) {
				return <span className="text-sm text-muted-foreground">-</span>
			}

			// Find preset name from presetId
			const presetId = row.original.presetId
			const presetName =
				presetId && typeof presetId === 'string'
					? presetsData?.presets?.find((p) => p.id === presetId)?.note ||
						presetId
					: null

			return (
				<div className="space-y-1 text-sm">
					{presetName && (
						<div className="flex items-center gap-1">
							<Badge variant="secondary" className="text-xs">
								Preset
							</Badge>
							<span className="text-xs">{presetName}</span>
						</div>
					)}
					{row.original.presetNumber && (
						<div className="flex items-center gap-1">
							<Badge variant="secondary" className="text-xs">
								RTP
							</Badge>
							<span className="font-mono text-xs">
								{row.original.presetNumber}%
							</span>
						</div>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'playerCount',
		header: 'จำนวนผู้เล่น',
		cell: ({ row }) => (
			<div className="text-center">
				{formatNumber(row.original.playerCount)}
			</div>
		),
	},
	{
		accessorKey: 'createdAt',
		header: 'วันที่สร้าง',
		cell: ({ row }) => (
			<div>{new Date(row.original.createdAt).toLocaleDateString('th-TH')}</div>
		),
	},
	{
		id: 'viewPlayers',
		header: 'ดูผู้เล่น',
		cell: ({ row }) => (
			<Link to={`/operator/tags/players?tagIds=${row.original.id}`}>
				<Button variant="outline" size="sm">
					<Users className="mr-2 h-4 w-4" />
					ดูผู้เล่น ({formatNumber(row.original.playerCount)})
				</Button>
			</Link>
		),
	},
	{
		id: 'actions',
		header: 'การดำเนินการ',
		cell: ({ row }) => (
			<div className="flex items-center gap-2">
				{hasWritePermission && (
					<>
						<Button
							variant="outline"
							size="sm"
							onClick={() => onEdit(row.original)}
						>
							<Edit className="h-4 w-4" />
						</Button>
						<Button
							variant="destructive"
							size="sm"
							onClick={() => onDelete(row.original)}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</>
				)}
			</div>
		),
	},
]
